import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CardBodyComponent, CardComponent } from '@groundworks/ngx-groundworks-ui';
import { observeUserRoles$ } from '../../models/roles.model';
import { QuoteInfo, SaleApprovalChecklistItems, SaleInfo } from '../../sdk/sdk';
import { ModalService } from '../../services/modal.service';
import {
  clearFlag,
  getAllChecklistItems,
  hasFlag,
  setFlag,
} from '../../utils/sale-approval-checklist.utils';

interface ChecklistTableItem {
  id: number;
  name: string;
  flagValue: SaleApprovalChecklistItems;
  systemVerified: boolean;
  managerVerified: boolean;
}

@Component({
  selector: 'app-sales-approval-checklist',
  imports: [CardComponent, CardBodyComponent],
  templateUrl: './sales-approval-checklist.component.html',
})
export class SalesApprovalChecklistComponent {
  quote = input<QuoteInfo | null>(null);
  opportunityId = input<string | null>(null);
  modalService = inject(ModalService);
  systemVerifiedSaleApprovalItems = input<SaleApprovalChecklistItems>(
    SaleApprovalChecklistItems.None,
  );
  completedSaleApprovalItems = input<SaleApprovalChecklistItems>(SaleApprovalChecklistItems.None);
  canSaleBeApproved = input<boolean>(false);

  userRoles = toSignal(observeUserRoles$(), { requireSync: true });

  canApproveSale = computed(() => {
    const roles = this.userRoles();
    return (
      roles?.SalesManager ||
      roles?.SuperUser ||
      roles?.Cfo ||
      roles?.VpOfOperations ||
      roles?.FieldSupport ||
      roles?.ServiceManager ||
      false
    );
  });

  isCheckboxDisabled = computed<boolean>(() => {
    const quote = this.quote();
    if (quote?.sale?.isReviewed) {
      return true;
    }
    if (!this.canSaleBeApproved()) {
      return true;
    }
    return !this.canApproveSale();
  });

  hasQualifyingProducts = computed<boolean>(() => {
    const quote = this.quote();
    if (!quote) {
      return true;
    }
    const quoted = quote.quotedProducts?.length ?? 0;
    const recurring = quote.recurringProducts?.length ?? 0;
    return quoted + recurring > 0;
  });

  isDisabled = computed<boolean>(() => {
    const quote = this.quote();

    if (quote?.sale?.isReviewed) {
      return true;
    }

    if (!this.canSaleBeApproved()) {
      return true;
    }

    if (!this.canApproveSale()) {
      return true;
    }

    if (!this.hasQualifyingProducts()) {
      return true;
    }

    const items = getAllChecklistItems();
    return !items.every((item) => hasFlag(this.completedSaleApprovalItems(), item.value));
  });

  completedSaleApprovalItemsChange = output<SaleApprovalChecklistItems>();
  saleCreated = output<{ quoteId: string; sale: SaleInfo }>();

  tableData = computed<ChecklistTableItem[]>(() => {
    const items = getAllChecklistItems();
    const completedFlags = this.completedSaleApprovalItems();
    const verificationStatus = this.systemVerifiedSaleApprovalItems();

    return items.map((item, index) => {
      return {
        id: index + 1,
        name: item.label,
        flagValue: item.value,
        systemVerified: hasFlag(verificationStatus, item.value),
        managerVerified: hasFlag(completedFlags, item.value),
      };
    });
  });

  onItemToggle(flag: SaleApprovalChecklistItems, checked: boolean): void {
    const currentValue = this.completedSaleApprovalItems();
    const newValue = checked ? setFlag(currentValue, flag) : clearFlag(currentValue, flag);
    this.completedSaleApprovalItemsChange.emit(newValue);
  }

  onCheckboxChange(flag: SaleApprovalChecklistItems, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.onItemToggle(flag, checkbox.checked);
  }

  approveSale(): void {
    const quote = this.quote();
    const opportunityId = this.opportunityId();

    if (!quote || !opportunityId) {
      console.error('SalesApprovalChecklistComponent: Quote or Opportunity ID is missing');
      return;
    }

    const modalRef = this.modalService.openConfirmSaleModal(opportunityId, quote);

    modalRef.result.then((sale) => {
      if (sale?.id) {
        this.saleCreated.emit({ quoteId: quote.id ?? '', sale });
      }
    });
  }
}
